import { useState, useEffect, useRef } from "react";
import {
  doc,
  updateDoc,
  deleteField,
  increment,
  getDocs,
  getDoc,
  collection,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import ReplyForm from "./ReplyForm";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import UserContextMenu from "./UserContextMenu";
import PublicProfileModal from "./PublicProfileModal";
import ReportUserModal from "./ReportUserModal";
import { UserIcon } from "@heroicons/react/24/solid";
import { containsBadWords } from "../utils/contentFilter";
import { notifyError } from "../utils/errorHandler";
import UserContextMenuVol2 from "./UserContextMenu";

const timeAgo = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate()
    : timestamp instanceof Date ? timestamp
    : new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const diff    = Date.now() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours   = Math.floor(minutes / 60);
  const days    = Math.floor(hours / 24);

  if (seconds < 60)  return "ahora mismo";
  if (minutes < 60)  return `hace ${minutes} min`;
  if (hours   < 24)  return `hace ${hours} h`;
  if (days    === 1) return "ayer";
  if (days    <  7)  return `hace ${days} días`;

  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
  }).format(date);
};

const formatDate      = timeAgo;
const formatReplyDate = timeAgo;
const getMediaInfo = (item) => {
  if (!item) return { url: "", type: "image" };

  // Si es un string (Cloudinary antiguo)
  if (typeof item === "string") {
    const isVideo = item.match(/\.(mp4|mov|avi|webm)$/i);
    return { url: item, type: isVideo ? "video" : "image" };
  }

  // Si es un objeto { url, type } (Firebase nuevo)
  if (typeof item === "object" && item.url && item.type) {
    return { url: item.url, type: item.type };
  }

  // Fallback
  return { url: "", type: "image" };
};

// Modal para mostrar la galería de imágenes
function ImageGalleryModal({ images, startIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  const prevImage = () => {
    setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  const nextImage = () => {
    setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!images.length) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 text-white text-3xl font-bold"
        aria-label="Cerrar"
      >
        &times;
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          prevImage();
        }}
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-white text-4xl font-bold"
        aria-label="Imagen anterior"
      >
        &#8249;
      </button>

      <img
        src={images[currentIndex]}
        alt={`Imagen ${currentIndex + 1}`}
        className="max-w-full max-h-[80vh] rounded shadow-lg"
        onClick={(e) => e.stopPropagation()}
      />

      <button
        onClick={(e) => {
          e.stopPropagation();
          nextImage();
        }}
        className="absolute right-5 top-1/2 transform -translate-y-1/2 text-white text-4xl font-bold"
        aria-label="Imagen siguiente"
      >
        &#8250;
      </button>

      <p className="text-white mt-4">
        {currentIndex + 1} / {images.length}
      </p>
    </div>
  );
}

function TweetItem({ tweet, onDelete,user }) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const hasLiked = tweet.likes?.users?.[userId];

  const [replies, setReplies] = useState([]);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ top: 0, left: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  // Menú 3 puntos y edición
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [editContent,     setEditContent]     = useState("");
  const [saving,          setSaving]          = useState(false);
  const optionsRef = useRef(null);

  // Estado para la galería de imágenes
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const navigate = useNavigate();
  const menuRef = useRef(null);

  //Para la foto de perfil que aparece en los feeds
  const [photoURL, setPhotoURL] = useState(null);
  const [showAllReplies, setShowAllReplies] = useState(false);

    useEffect(() => {
  if (!tweet?.userId) return;

  const fetchUserPhoto = async () => {
    try {
      const userDoc = await getDoc(doc(db, "users", tweet.userId));
      if (userDoc.exists()) {
        setPhotoURL(userDoc.data().photoURL || null);
      } else {
        setPhotoURL(null);
      }
    } catch (error) {
      console.error("Error al obtener la foto del usuario:", error);
      setPhotoURL(null);
    }
  };

  fetchUserPhoto();
}, [tweet?.userId]);



  // Cerrar menú contextual al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setContextMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cerrar menú de opciones (3 puntos) al hacer clic fuera
  useEffect(() => {
    if (!showOptionsMenu) return;
    const handleClose = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) {
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClose);
    return () => document.removeEventListener("mousedown", handleClose);
  }, [showOptionsMenu]);

  // Cargar replies desde subcolección
  useEffect(() => {
    const fetchReplies = async () => {
      try {
        const repliesSnapshot = await getDocs(
          collection(db, "tweets", tweet.id, "replies")
        );
        const repliesData = repliesSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((reply) => !reply.deleted);
        setReplies(repliesData);
      } catch (err) {
        console.error("Error cargando replies:", err);
      }
    };
    fetchReplies();
  }, [tweet.id]);

  const handleLikeClick = async () => {
    if (!userId) return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Debes iniciar sesión para dar like", showConfirmButton: false, timer: 3000 });
    if (currentUser?.status === "pending") {
      return Swal.fire({
        icon: "info",
        title: "Cuenta pendiente",
        text: "Podrás interactuar con publicaciones una vez que tu cuenta sea verificada.",
        confirmButtonColor: "#3b82f6",
      });
    }
    const tweetRef = doc(db, "tweets", tweet.id);

    try {
      if (hasLiked) {
        await updateDoc(tweetRef, {
          [`likes.users.${userId}`]: deleteField(),
          "likes.count": increment(-1),
        });
      } else {
        await updateDoc(tweetRef, {
          [`likes.users.${userId}`]: { name: currentUser.displayName },
          "likes.count": increment(1),
        });
      }
    } catch (err) {
      console.error("Error actualizando like:", err);
      notifyError("No se pudo actualizar el like.");
    }
  };

 const handleReply = async (text) => {
  if (!userId) return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Debes iniciar sesión para responder", showConfirmButton: false, timer: 3000 });
  if (currentUser?.status === "pending") {
    return Swal.fire({
      icon: "info",
      title: "Cuenta pendiente",
      text: "Podrás responder publicaciones una vez que tu cuenta sea verificada.",
      confirmButtonColor: "#3b82f6",
    });
  }

  const trimmedText = text.trim();

  if (!trimmedText) {
    return Swal.fire("Oops", "La respuesta está vacía.", "warning");
  }

  if (containsBadWords(trimmedText)) {
    return Swal.fire(
      "Lenguaje no permitido",
      "Tu respuesta contiene palabras ofensivas o inapropiadas. Por favor, modifícala antes de enviarla.",
      "error"
    );
  }

  const replyId = `${Date.now()}-${Math.random()
    .toString(36)
    .substring(2, 10)}`;

  const replyData = {
    id: replyId,
    userId,
    userName: currentUser.displayName,
    text: trimmedText,
    date: new Date(),
    deleted: false,
  };

  try {
    await setDoc(doc(db, "tweets", tweet.id, "replies", replyId), replyData);
    setReplies((prev) => [...prev, replyData]);
  } catch (err) {
    console.error("Error enviando reply:", err);
    Swal.fire("Error", "No se pudo enviar la respuesta.", "error");
  }
};


  const handleDeleteReplyClick = async (reply) => {
    if (!userId) return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Debes iniciar sesión", showConfirmButton: false, timer: 3000 });
    if (reply.userId !== userId) return Swal.fire({ toast: true, position: "top-end", icon: "error", title: "No puedes eliminar comentarios de otros usuarios", showConfirmButton: false, timer: 3000 });

    const result = await Swal.fire({
      title: "¿Eliminar este comentario?",
      text: "No podrás revertir esta acción",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const replyRef = doc(db, "tweets", tweet.id, "replies", reply.id);
        await updateDoc(replyRef, { deleted: true });
        setReplies((prev) => prev.filter((r) => r.id !== reply.id));
        Swal.fire("Eliminado", "El comentario fue eliminado correctamente.", "success");
      } catch (error) {
        console.error("Error eliminando reply:", error);
        Swal.fire("Error", "No se pudo eliminar el comentario.", "error");
      }
    }
  };

  const openUserContextMenu = (e, user) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = menuRef.current?.getBoundingClientRect() || { top: 0, left: 0 };

    const top = rect.top - containerRect.top + rect.height / 2;
    const left = rect.left - containerRect.left + rect.width + 8;

    setContextMenuPos({ top, left });
    setSelectedUser(user);
    setContextMenuOpen(true);
  };

  const handleViewProfile = () => setShowProfileModal(true);
  const handleSendMessage = () => navigate(`/chat/${selectedUser.uid}`);
  const handleReport = () => setShowReportModal(true);

  // Editar tweet
  const handleEdit = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) return;
    if (containsBadWords(trimmed)) {
      return Swal.fire("Lenguaje no permitido", "Tu publicación contiene palabras ofensivas.", "error");
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "tweets", tweet.id), {
        content: trimmed,
        editedAt: serverTimestamp(),
      });
      setShowEditModal(false);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Publicación actualizada", showConfirmButton: false, timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire("Error", "No se pudo editar la publicación.", "error");
    }
    setSaving(false);
  };

  // Abrir modal galería con la imagen clickeada
  const handleImageClick = (index) => {
    setGalleryStartIndex(index);
    setShowImageGallery(true);
  };

  return (
  <div
    ref={menuRef}
    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-2xl shadow-lg relative max-w-xl mx-auto transition-all"
  >
    {/* Header */}
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
          {photoURL ? (
            <img
              src={photoURL}
              alt="Foto de perfil"
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <UserIcon className="w-10 h-10 text-gray-400" />
          )}
        </div>
        <div>
          <button
            onClick={(e) =>
              openUserContextMenu(e, {
                uid: tweet.userId,
                nombre: tweet.userName,
                nickname: tweet.nickname || "",
                carrera: tweet.carrera || "",
                role: tweet.role || "",
                email: tweet.email || "",
              })
            }
            className="text-base font-semibold text-gray-900 dark:text-white hover:underline"
          >
            {tweet.userName || "Anónimo"}
          </button>
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            {formatDate(tweet.date)}
            {tweet.editedAt && <span className="italic opacity-70">(editado)</span>}
          </div>
        </div>
      </div>
      {tweet.userId === userId && (
        <div className="relative" ref={optionsRef}>
          <button
            onClick={() => setShowOptionsMenu((v) => !v)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            title="Opciones"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          {showOptionsMenu && (
            <div className="absolute right-0 top-8 z-30 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
              <button
                onClick={() => { setEditContent(tweet.content); setShowEditModal(true); setShowOptionsMenu(false); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar
              </button>
              <button
                onClick={() => { onDelete(tweet.id); setShowOptionsMenu(false); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Eliminar
              </button>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Content */}
    <div className="max-w-full overflow-hidden">
      <p className="mb-3 text-gray-800 dark:text-gray-100 text-sm leading-relaxed break-words">
        {tweet.content.split(/(https?:\/\/[^\s]+)/g).map((part, index) =>
          part.match(/^https?:\/\//) ? (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline break-all"
            >
              {part}
            </a>
          ) : (
            part
          )
        )}
      </p>
    </div>



    {/* Media */}
{tweet.media?.length > 0 && (
  <div
    className={`grid gap-2 mb-3 rounded-xl overflow-hidden ${
      tweet.media.length === 1
        ? "grid-cols-1"
        : tweet.media.length === 2
        ? "grid-cols-2"
        : "grid-cols-2"
    }`}
  >
    {tweet.media.map((item, i) => {
      const { url, type } = getMediaInfo(item);

      return type === "video" ? (
        <video
          key={i}
          controls
          className="rounded-xl w-full max-h-60 object-cover shadow-sm"
        >
          <source src={url} type="video/mp4" />
          Tu navegador no soporta este video.
        </video>
      ) : (
        <img
          key={i}
          src={url}
          alt={`media-${i}`}
          className="rounded-xl w-full max-h-60 object-cover cursor-pointer transition-transform hover:scale-105"
          onClick={() => handleImageClick(i)}
        />
      );
    })}
  </div>
)}


    {/* Actions */}
    <div className="flex items-center justify-between mb-2">
      <div className="relative group">
        <button
          onClick={handleLikeClick}
          className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
        >
          {hasLiked ? "💙" : "🤍"} {tweet.likes?.count || 0}
        </button>
        {tweet.likes?.count > 0 && tweet.likes.users && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 p-2 bg-gray-900 text-white rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto z-20 text-xs max-h-40 overflow-y-auto">
            <p className="font-semibold mb-1">Les gustó este tweet:</p>
            <ul>
              {Object.values(tweet.likes.users).map((user, i) => (
                <li key={i}>{user.name || "Usuario desconocido"}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>

    {/* Responder */}
    <div className="mt-3">
      <ReplyForm onReply={handleReply} />
    </div>

    {/* Respuestas */}
    {replies.length > 0 && (
      <div className="mt-4 space-y-3">
        <h4 className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Respuestas:</h4>

        {(showAllReplies ? replies : replies.slice(0, 2)).map((reply) => (
          <div
            key={reply.id}
            className="bg-gray-100 dark:bg-gray-700 p-3 rounded-xl shadow-sm flex justify-between items-start"
          >
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                <strong>
                  <button
                    onClick={(e) =>
                      openUserContextMenu(e, {
                        uid: reply.userId,
                        nombre: reply.userName,
                      })
                    }
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    {reply.userName || "Anónimo"}
                  </button>
                </strong>
                : {reply.text}
              </p>
              <span className="text-xs text-gray-500">{formatReplyDate(reply.date)}</span>
            </div>
            {reply.userId === userId && (
              <button
                onClick={() => handleDeleteReplyClick(reply)}
                className="text-red-500 hover:text-red-600 text-sm ml-3"
                title="Eliminar respuesta"
              >
                🗑️
              </button>
            )}
          </div>
        ))}

        {/* Botón Ver más / Ver menos */}
        {replies.length > 2 && (
          <div>
            <button
              onClick={() => setShowAllReplies(!showAllReplies)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showAllReplies ? "Ver menos comentarios" : `Ver más comentarios (${replies.length - 2})`}
            </button>
          </div>
        )}
      </div>
    )}

      {contextMenuOpen && selectedUser && (
        <UserContextMenu
          position={contextMenuPos}
          user={selectedUser}
          onViewProfile={handleViewProfile}
          onSendMessage={handleSendMessage}
          onReport={handleReport}
          onClose={() => setContextMenuOpen(false)}
        />
      )}

      <PublicProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={selectedUser}
      />

      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        user={selectedUser}
      />

      {showImageGallery && (
        <ImageGalleryModal
          images={tweet.media
            .map((item) => (typeof item === "string" ? item : item?.url || ""))
            .filter((url) => url && !url.match(/\.(mp4|mov|avi|webm)$/i))
          }
          startIndex={galleryStartIndex}
          onClose={() => setShowImageGallery(false)}
        />
      )}

      {/* ── Modal de edición ─────────────────────────────────────────────── */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-bold text-gray-900 dark:text-white text-lg">Editar publicación</h3>
            <textarea
              className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[120px]"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              maxLength={500}
              autoFocus
            />
            <p className="text-xs text-gray-400 text-right -mt-2">{editContent.length}/500</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleEdit}
                disabled={saving || !editContent.trim()}
                className="flex-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition disabled:opacity-50"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TweetItem;
